import { CollectQueueStatus, TaskStatuses } from "../../../../config";
import styled, { keyframes } from "styled-components";
import { formatOnlyMinutes } from "../../../../utils/date";
import { isEmptyObject } from "../../../../utils/objects";

interface PickupScreenContent {
	title: string;
	subTitle?: string;
	subtext: string | JSX.Element;
	icon: string;
	additionalInfo?: string;
	showETA: boolean;
}

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Styled Components
const ModalOverlay = styled.div`
	height: 100%;
	position: fixed;
	inset: 0;
	background-color: black;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1rem;
	animation: ${fadeIn} 0.3s ease-in-out;
	background: #282828;
	z-index: 114;
`;

const ModalContainer = styled.div`
	height: 100%;
	width: 100%;
	max-width: 24rem;
	text-align: center;
	color: white;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const Title = styled.h1`
	font-size: 1.5rem;
	font-weight: 600;
	margin-bottom: 2rem;
`;

const Icon = styled.img`
	width: 12rem;
	height: 12rem;
	margin: 0 auto 2rem;
`;

const SubTitleText = styled.div`
	font-size: 1.125rem;
	font-weight: 600;
	margin-bottom: 1.5rem;
`;

const SubText = styled.p`
	color: #9ca3af;
	font-size: 0.875rem;
	margin-bottom: 1rem;
	width: 80%;
	text-align: center;
`;

const AdditionalInfo = styled.p`
	position: absolute;
	bottom: 1rem;
	color: #9ca3af;
	font-size: 0.875rem;
	padding: inherit;
	max-width: inherit;
`;

function getQueueStatusLabel(collectTaskQueueStatus: string): PickupScreenContent {
	switch (collectTaskQueueStatus) {
		case CollectQueueStatus.OPEN:
			return {
				title: "Collect Your Dish",
				subTitle: "",
				subtext:
					"Your bowl will be in the collection area for you to collect, please keep an eye on this screen for regular update.",
				icon: "https://res.cloudinary.com/xook/image/upload/v1739186987/app.xook.ai/Wait_icon_tz1fo4.png",
				showETA: true
			};

		case CollectQueueStatus.QUEUED:
			return {
				title: "Collect Your Dish",
				subTitle: "",
				subtext:
					"The kiosk is currently serving dishes for other customers who requested before you. Kindly wait for your turn and keep an eye on the screen for updates.",
				icon: "https://res.cloudinary.com/xook/image/upload/v1739186987/app.xook.ai/Wait_icon_tz1fo4.png",
				showETA: true
			};

		case CollectQueueStatus.COLLECTING:
			return {
				title: "Collect Your Dish",
				subTitle: "Mix & Enjoy Your Bowl",
				subtext: "We recommend you to mix the bowl a bit to get every flavor in every bite.",
				icon: "https://res.cloudinary.com/xook/image/upload/v1739186964/app.xook.ai/Mix_n_Enjoy_nh95r7.png",
				additionalInfo: "Once collected, you will be redirected automatically.",
				showETA: false
			};

		default:
			//The following condition should never be reached.
			//If this is reached that means something went wrong in the checkForItemsUpforCollection function
			return {
				title: "Invalid Status",
				subtext: "Something went wrong here. Contact us for more info.",
				icon: "https://res.cloudinary.com/xook/image/upload/v1739186996/app.xook.ai/Icon_for_item_discrepancy_rcbtzj.png",
				showETA: false
			};
	}
}

function checkForItemsUpforCollection(activeItemData: any) {
	if (!isEmptyObject(activeItemData)) {
		return activeItemData;
	}
	return null;
}

function getRemainingTime(estimatedCollectStartTime: any) {
	if (typeof estimatedCollectStartTime !== "undefined" && estimatedCollectStartTime !== null) {
		return Math.max(1, Math.floor(estimatedCollectStartTime - Date.now()));
	}
	return 1;
}

const CollectionModalContent = (props: any) => {
	const { activeItem } = props;

	const content = getQueueStatusLabel(activeItem.collectTaskQueueStatus);
	const remainingTime = getRemainingTime(activeItem.estimatedCollectStartTime);

	return (
		<ModalOverlay>
			<ModalContainer>
				<Title>{content?.title}</Title>
				<Icon src={content.icon} />
				{content.showETA ? (
					<SubTitleText>In less than {formatOnlyMinutes(remainingTime)}</SubTitleText>
				) : (
					<SubTitleText>{content.subTitle}</SubTitleText>
				)}
				<SubText>{content?.subtext}</SubText>
				{content?.additionalInfo ? (
					<>
						<AdditionalInfo>{content.additionalInfo}</AdditionalInfo>
					</>
				) : (
					""
				)}
			</ModalContainer>
		</ModalOverlay>
	);
};

const CollectionModal = (props: any) => {
	const { orderData } = props;
	const activeItem = checkForItemsUpforCollection(orderData?.activeItemData);

	if (activeItem) {
		return <CollectionModalContent activeItem={activeItem} />;
	} else {
		return <></>;
	}
};

export default CollectionModal;
